// Store di modulo per la generazione in corso del Chatbot (task 452).
//
// PERCHÉ FUORI DA REACT
// Uscendo dal chatbot verso un'altra pagina della dashboard il componente si smonta
// e con lui sparirebbe lo stato della generazione. La fetch però NON viene abortita
// (nessun cleanup la interrompe) e il backend salva comunque la risposta a fine
// generazione. Tenendo lo stato qui, in un singleton di modulo che vive quanto la
// scheda del browser, rientrando nella pagina ci si riaggancia allo stream invece di
// ripartire da una chat vuota.
//
// LIMITE NOTO: un refresh o la chiusura della scheda azzerano questo store. In quel
// caso la risposta non si perde comunque, perché è già a database: basta riaprire la
// conversazione. Fuori ambito per il task, che copre il cambio pagina interno.

const EMPTY = {
    sending: false,
    convId: null,
    placeholderId: null,
    messages: [], // vista ottimistica: base + messaggio utente + bolla assistente
    toolStatus: "",
    error: "",
    interrupted: false,
    ctrl: null, // AbortController, per lo Stop
};

let state = { ...EMPTY };
const listeners = new Set();

// Intervallo minimo fra due aggiornamenti della bolla in streaming (~25 al secondo:
// fluido all'occhio, un quarto dei render rispetto a "uno per token").
const TOKEN_FLUSH_MS = 40;

const emit = () => {
    for (const listener of listeners) listener(state);
};

const patch = (changes) => {
    state = { ...state, ...changes };
    emit();
};

// Aggiorna un singolo messaggio della vista ottimistica (usato per i token in arrivo).
const patchMessage = (msgId, changes) =>
    patch({
        messages: state.messages.map((m) => (m.id === msgId ? { ...m, ...changes } : m)),
    });

export const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const getStreamState = () => state;

// C'è una generazione in volo su questa conversazione? Serve al componente per
// decidere, al mount, se riagganciarsi invece di ricaricare dal DB.
export const isStreamingFor = (convId) => state.sending && state.convId === convId;

export const abortStream = () => state.ctrl?.abort();

export const resetStream = () => {
    state = { ...EMPTY };
    emit();
};

// Consuma uno stream SSE riempiendo la bolla placeholder. Identica alla logica che
// stava in Chatbot.consumeStream, ma scrive nello store invece che nello state del
// componente: è questo che le permette di continuare a pagina smontata.
//
// `iterable`  async generator da chatbotApi (streamMessage / streamRegenerate)
// `messages`  vista ottimistica iniziale (base + utente + placeholder)
// `onDone`    callback opzionale a fine generazione, per ricaricare i dati reali.
//             Attenzione: se la pagina è smontata la callback non ha effetto visibile,
//             per questo il componente ricarica anche al proprio mount.
export const runStream = async ({ iterable, ctrl, convId, placeholderId, messages, onDone }) => {
    patch({
        sending: true,
        convId,
        placeholderId,
        messages,
        toolStatus: "",
        error: "",
        interrupted: false,
        ctrl,
    });

    // I token arrivano anche a decine al secondo, e ogni patch ridisegna la pagina:
    // si accumulano e si riversano nella bolla al massimo ogni TOKEN_FLUSH_MS, in una
    // sola patch (testo + azzeramento dell'indicatore). Prima di ogni altro evento e a
    // fine stream si svuota il buffer, così l'ordine resta quello di arrivo.
    let pending = "";
    let flushTimer = null;
    // Strumenti annunciati da quando è arrivata l'ultima parola: da quando il backend
    // esegue in parallelo gli strumenti web, di eventi `tool` ne arrivano più di uno
    // insieme e mostrarne solo l'ultimo direbbe all'utente meno di quello che sta
    // davvero succedendo. Si azzera al flush dei token, come toolStatus.
    let strumentiDelGiro = [];
    const flushTokens = () => {
        if (flushTimer) {
            clearTimeout(flushTimer);
            flushTimer = null;
        }
        if (!pending) return;
        const testo = pending;
        pending = "";
        strumentiDelGiro = [];
        patch({
            toolStatus: "",
            messages: state.messages.map((m) => (m.id === placeholderId ? { ...m, content: (m.content || "") + testo } : m)),
        });
    };

    try {
        for await (const ev of iterable) {
            if (ev.type === "token") {
                pending += ev.text;
                if (!flushTimer) flushTimer = setTimeout(flushTokens, TOKEN_FLUSH_MS);
                continue;
            }
            flushTokens();
            if (ev.type === "tool") {
                strumentiDelGiro.push(ev.name);
                // Più strumenti nello stesso giro: girano IN PARALLELO, e l'etichetta lo
                // dice invece di mostrare solo l'ultimo arrivato.
                if (strumentiDelGiro.length > 1) {
                    const n = strumentiDelGiro.length;
                    const tutti = (nome) => strumentiDelGiro.every((s) => s === nome);
                    patch({
                        toolStatus: tutti("cerca_sul_web")
                            ? `Sto cercando ${n} cose sul web insieme…`
                            : tutti("leggi_pagina_web")
                              ? `Sto leggendo ${n} pagine insieme…`
                              : `Sto consultando ${n} fonti insieme…`,
                    });
                    continue;
                }
                // Il modello sta generando un file/immagine: indicatore transitorio.
                if (ev.name === "cerca_sul_web") {
                    // La query può essere lunga: se ne mostra l'inizio, quanto basta a
                    // far capire COSA sta cercando.
                    const q = (ev.query || "").trim();
                    const breve = q.length > 48 ? `${q.slice(0, 48)}…` : q;
                    patch({ toolStatus: breve ? `Sto cercando sul web: "${breve}"` : "Sto cercando sul web…" });
                } else if (ev.name === "leggi_pagina_web") {
                    let dominio = "";
                    try {
                        dominio = ` ${new URL(ev.url).hostname}`;
                    } catch {
                        /* url assente o malformato: resta l'indicatore generico */
                    }
                    patch({ toolStatus: `Sto leggendo la pagina${dominio}…` });
                } else if (ev.name === "genera_grafica") {
                    // Composizione sui template del brand (fase 3): "(citazione, LF23)".
                    const dettagli = [ev.formato, ev.brand].filter(Boolean).join(", ");
                    patch({ toolStatus: `Sto componendo la grafica${dettagli ? ` (${dettagli})` : ""}…` });
                } else if (ev.name === "genera_immagine") {
                    patch({ toolStatus: "Sto generando l'immagine…" });
                } else if (ev.name === "modifica_immagine") {
                    patch({ toolStatus: "Sto modificando l'immagine…" });
                } else {
                    const nome = ev.filename ? ` "${ev.filename}"` : "";
                    // Fra parentesi tipo e brand (task 453): "(volantino, LOCAL)".
                    const dettagli = [ev.tipo, ev.brand].filter(Boolean).join(", ");
                    patch({ toolStatus: `Sto generando il file${nome}${dettagli ? ` (${dettagli})` : ""}…` });
                }
            } else if (ev.type === "done") {
                patch({ toolStatus: "" });
            } else if (ev.type === "error") {
                patch({ toolStatus: "", error: ev.error || "Errore durante la generazione." });
            }
        }
    } catch (err) {
        flushTokens();
        if (err?.name === "AbortError") {
            // Stop volontario: teniamo il testo parziale e marchiamo l'interruzione.
            patch({ interrupted: true });
            patchMessage(placeholderId, { interrupted: true });
        } else {
            patch({ error: err.message || "Errore durante l'operazione." });
        }
    } finally {
        flushTokens();
        patch({ sending: false, toolStatus: "", ctrl: null });
        try {
            // `interrupted` = lo stream è stato troncato dal CLIENT (ripiego di quando il
            // server non risponde allo Stop). In quel caso non si ricarica dal DB, dove
            // la risposta non è ancora stata scritta. Lo Stop normale passa dal server,
            // che salva il parziale: lì il ricaricamento è giusto e va fatto.
            await onDone?.({ convId, error: state.error, interrupted: state.interrupted });
        } catch {
            /* il ricaricamento fallito non deve rompere la fine dello stream */
        }
    }
};
